// FILE: managedAgentMode.ts
// Purpose: The single reading of which surfaces a managed agent's `mode` allows,
//          shared by the server (which enforces it) and the web (which must not
//          offer what the server would refuse).
// Layer: shared runtime utility
// Depends on: @taskin-code/contracts (type only)
// Exports: `canRunAsPrimary`, `canBeSubagent`, `canRunSideChat`,
//          `MANAGED_AGENT_MODE_OPTIONS`, `MANAGED_AGENT_MODE_LABELS`,
//          `describeManagedAgentMode`.

import type { ManagedAgentMode } from "@taskin-code/contracts";

/**
 * Why this lives in `shared` and not next to either consumer:
 *
 * The bug that migration 022 closed was "what the picker offers" and "what the
 * server accepts" being two separate answers to one question. Mode adds five
 * more places that have to agree — `resolveUsableBy`, side-chat authorization,
 * the subagent picker, the admin routes that pin a default agent, and the
 * project-config fallback — so the answer is written once, here, and every one
 * of them asks. These are pure predicates over three literals; the cost of
 * sharing them is an import, and the cost of not sharing them is drift.
 *
 * Not merged into `shared/agents.ts`: that file is the CLI launcher catalog
 * (`claude`/`codex` binaries spawned into a terminal), which shares nothing
 * with managed agents but the word "agent".
 */

/**
 * Whether a person may pick this agent directly — a new thread, the chat's
 * model picker, a fork, a handoff, or as a default/allowlisted agent.
 *
 * This is the predicate that answers "can a user run it at all?", so it is also
 * what gates the admin fields that *pre-select* an agent on someone's behalf.
 */
export function canRunAsPrimary(mode: ManagedAgentMode): boolean {
  return mode === "primary" || mode === "general";
}

/** Whether this agent may be wired as another agent's delegation target. */
export function canBeSubagent(mode: ManagedAgentMode): boolean {
  return mode === "subagent" || mode === "general";
}

/**
 * Whether this agent may be offered for a side chat.
 *
 * Derived from `canRunAsPrimary` rather than written out again: a side chat is a
 * person choosing an agent from a menu, so the mode that says "a person never
 * chooses this one" has to exclude it too. The agent's own `sideChatEnabled`
 * flag still applies on top — this only says the mode does not forbid it.
 */
export function canRunSideChat(mode: ManagedAgentMode): boolean {
  return canRunAsPrimary(mode);
}

/**
 * A stored/received mode string as one of the three literals, falling back to
 * `general`.
 *
 * The column is plain TEXT and callers that read it with a hand-written query
 * (the admin routes do, to avoid hydrating a whole record for one predicate) get
 * a `string` back. `general` is the safe fallback: it is the pre-mode behaviour
 * and the value the migration backfilled, so an unreadable mode degrades into
 * "as it always was" rather than into an agent that vanishes from every picker.
 */
export function parseManagedAgentMode(raw: string): ManagedAgentMode {
  return raw === "primary" || raw === "subagent" ? raw : "general";
}

/** Selector order for the editor: widest first, then the two narrow ones. */
export const MANAGED_AGENT_MODE_OPTIONS: ReadonlyArray<ManagedAgentMode> = [
  "general",
  "primary",
  "subagent",
];

/**
 * English labels, matching the rest of the settings panel. The UI language is
 * not this module's call to make — it only owns the wording being consistent
 * wherever a mode is shown.
 */
export const MANAGED_AGENT_MODE_LABELS: Readonly<Record<ManagedAgentMode, string>> = {
  general: "General",
  primary: "Primary",
  subagent: "Subagent",
};

/** One-line explanation of a mode, for the editor's help text. */
export function describeManagedAgentMode(mode: ManagedAgentMode): string {
  switch (mode) {
    case "primary":
      return "Users can pick it; it cannot be another agent's subagent.";
    case "subagent":
      return "Only reachable through delegation — never offered in a picker or a side chat.";
    case "general":
      return "Users can pick it and other agents can delegate to it.";
  }
}
